// @ts-nocheck
import classNames from 'classnames'
import { arrayOf, bool, number, oneOf, shape, string } from 'prop-types'
import { type FunctionComponent } from 'react'
import * as React from 'react'

import './ProductImage.scss'
import {
  AVAILABLE_PRESCRIPTION_TYPES,
  AVAILABLE_PRODUCT_TEMPLATES,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  PRODUCT_TEMPLATES,
} from 'shared/consts/productTemplates'
import { InferPropsWithDefaults } from 'shared/utils/propTypes'
import ImageWithDiscountBadge from 'views/components/molecules/ImageWithDiscountBadge'
import ProductBonusBadge from 'views/components/organisms/ProductImageGallery/ProductImageGalleryOverlay/ProductBonusBadge'
import ProductRedPointBadge from 'views/components/organisms/ProductImageGallery/ProductImageGalleryOverlay/ProductRedPointBadge'
import { getBonus } from 'views/components/organisms/ProductTemplate/helpers'
import { sizesPropType } from 'views/utils/propTypes/img'
import { shouldRenderBadge } from 'views/utils/redPointsUtils'

const propTypes = {
  discount: number,
  imageUrl: string,
  isERX: bool,
  lazyLoad: bool,
  sizes: sizesPropType,
  minWidth: number,
  maxWidth: number,
  multiplierRP: number,
  name: string,
  prescriptionMedicine: bool,
  prescriptionType: oneOf(AVAILABLE_PRESCRIPTION_TYPES.ALL),
  prices: arrayOf(shape({})),
  productTemplate: oneOf(AVAILABLE_PRODUCT_TEMPLATES),
  redPoints: number,
  redPointsIsActive: bool,
  redPointsMultiplierIsValid: bool,
  srcHeight: number,
  srcWidth: number,
  thumbnail: bool,
  showBadges: bool,
  imageAttrs: shape({}),
  tighterThumbnailWithDarkBackground: bool,
  productImageBackgroundDark: bool,
}

const defaultProps = {
  discount: null,
  imageUrl: null,
  isERX: false,
  lazyLoad: null,
  sizes: null,
  minWidth: null,
  maxWidth: null,
  multiplierRP: null,
  name: null,
  prescriptionMedicine: false,
  prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  prices: null,
  productTemplate: PRODUCT_TEMPLATES.OTC,
  redPoints: null,
  redPointsIsActive: false,
  redPointsMultiplierIsValid: false,
  srcHeight: null,
  srcWidth: null,
  thumbnail: false,
  showBadges: true,
  imageAttrs: null,
  tighterThumbnailWithDarkBackground: false,
  productImageBackgroundDark: false,
}

type ProductImageProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

/**
 * todo add support for promotion labels (SAEFRNT-2827) +
 */

const ProductImage: FunctionComponent<ProductImageProps> = ({
  discount,
  imageAttrs,
  imageUrl,
  lazyLoad,
  sizes,
  minWidth,
  maxWidth,
  multiplierRP,
  name,
  prescriptionMedicine,
  prescriptionType,
  prices,
  productTemplate,
  redPoints,
  redPointsIsActive,
  redPointsMultiplierIsValid,
  srcHeight,
  srcWidth,
  thumbnail,
  showBadges,
  tighterThumbnailWithDarkBackground,
  productImageBackgroundDark,
  isERX,
}): React.ReactElement => {
  const bonus: number =
    showBadges && prescriptionMedicine ? getBonus(prices, prescriptionType) : 0
  const hasRedPointsBadge: boolean =
    showBadges &&
    !bonus &&
    !discount &&
    shouldRenderBadge({
      redPoints,
      redPointsIsActive,
      productTemplate,
    })

  let discountPercent: number
  if (bonus) {
    discountPercent = 0
  } else {
    discountPercent = showBadges ? discount : 0
  }

  const productImageClassName: string = classNames('o-ProductImage', {
    'o-ProductImage--productImageDarkBackground': productImageBackgroundDark,
  })

  return (
    <div className={productImageClassName}>
      {bonus ? (
        <div className="o-ProductImage__badge">
          <ProductBonusBadge
            className="o-ProductImage__bonusBadge"
            prescriptionType={prescriptionType}
            bonus={bonus}
            isClientsideControlled
          />
        </div>
      ) : null}
      {hasRedPointsBadge && (
        <div className="o-ProductImage__badge">
          <ProductRedPointBadge
            {...{
              redPoints,
              redPointsMultiplierIsValid,
              multiplierRP,
              prescriptionType,
            }}
            className="o-ProductImage__redPointBadge"
          />
        </div>
      )}
      <ImageWithDiscountBadge
        {...{
          discountPercent,
          alt: name,
          imageAttrs,
          imageUrl,
          isERX,
          sizes,
          minWidth,
          maxWidth,
          thumbnail,
          srcWidth,
          srcHeight,
          lazyLoad,
          tighterThumbnailWithDarkBackground,
          prescriptionMedicine,
        }}
      />
    </div>
  )
}

ProductImage.propTypes = propTypes
ProductImage.defaultProps = defaultProps

export * from './ProductImageListCart'
export default ProductImage
